/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { type MenuInputOption } from '@saviynt/design-system';

import type { DataProvider, ProviderOption } from '../../../shared/types';

import { DATA_PROVIDER } from '../../FormBuilder';

// ---- Safe helpers -----------------------------------------------------------

interface OptionLike {
  value: string | number;
  label?: string;
  isChecked?: boolean;
  secondaryLabel?: string;
}

const isOptionLike = (x: unknown): x is OptionLike => {
  if (typeof x !== 'object' || x === null) {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (x as any).value;

  return typeof v === 'string' || typeof v === 'number';
};

const toStringValue = (v: unknown): string | null => {
  if (v === null || v === undefined) {
    return null;
  }

  return String(v);
};

const toStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) {
    return v
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (isOptionLike(item)) {
          return String(item.value);
        }

        return null;
      })
      .filter((item): item is string => item !== null);
  }

  if (typeof v === 'string') {
    return v
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  // Handle single numeric values
  if (typeof v === 'number') {
    return [String(v)];
  }

  return [];
};

// ---- SingleSelect -----------------------------------------------------------

/**
 * True when the option should drive MenuInput selection (excludes cascade clear
 * `{ label: '', value: '' }` and other empty values).
 */
export function isMeaningfulSingleSelectValue(
  option: unknown
): option is MenuInputOption {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return false;
  }

  const v = (option as MenuInputOption).value;

  if (v == null) {
    return false;
  }

  if (typeof v === 'string' && v === '') {
    return false;
  }

  return true;
}

/**
 * Processes a raw entity value into a MenuInputOption for SingleSelect
 */
export function processSingleSelectValue(
  entityValue: unknown
): MenuInputOption | null {
  const stringValue = toStringValue(entityValue);

  if (stringValue === null) {
    return null;
  }

  return {
    value: stringValue,
    label: stringValue,
    isChecked: true, // actual checked state will be normalized by applyValueMatching
  };
}

/**
 * Update flows often store a primitive (string/number) while provider options
 * may use a different runtime type for `value` (e.g. numeric JSON). Resolving
 * against loaded options yields the same canonical {@link MenuInputOption} shape
 * hosts get when they pass `{ label, value, isChecked }` explicitly.
 */
export function resolveProcessedSingleSelectFromOptions(
  entityValue: unknown,
  options: MenuInputOption[]
): MenuInputOption | null {
  const provisional = processSingleSelectValue(entityValue);

  if (!provisional) {
    return null;
  }

  if (options.length === 0) {
    return provisional;
  }

  const needle = String(provisional.value);

  const match = options.find(
    (o) =>
      String(o.value) === needle ||
      (o.label !== undefined && o.label !== null && String(o.label) === needle)
  );

  return match ? { ...match, isChecked: true } : provisional;
}

// ---- MultiSelect ------------------------------------------------------------

/**
 * Processes raw entity values into MenuInputOption array for MultiSelect
 */
export function processMultiSelectValues(
  entityValues: unknown,
  dataProvider: DataProvider
): MenuInputOption[] {
  if (entityValues === null || entityValues === undefined) {
    return [];
  }

  if (dataProvider.type === DATA_PROVIDER.FIXED) {
    const valuesToProcess = toStringArray(entityValues);

    if (valuesToProcess.length === 0) {
      return [];
    }

    const fixedOptions: ProviderOption[] = dataProvider.fixed;
    const matches: ProviderOption[] = fixedOptions.filter((opt) => {
      const val = String(opt.value);
      const lbl = opt.label ? String(opt.label) : undefined;

      return (
        valuesToProcess.includes(val) ||
        (lbl !== undefined && valuesToProcess.includes(lbl))
      );
    });

    return matches.map<MenuInputOption>((opt) => ({
      value: opt.value,
      label: opt.label,
      isChecked: true,
      ...(opt.secondaryLabel && { secondaryLabel: opt.secondaryLabel }),
    }));
  }

  if (
    dataProvider.type === DATA_PROVIDER.HTTP ||
    dataProvider.type === DATA_PROVIDER.SQL ||
    dataProvider.type === DATA_PROVIDER.DATASTORE
  ) {
    const valuesToProcess = toStringArray(entityValues);

    if (valuesToProcess.length === 0) {
      return [];
    }

    return valuesToProcess.map<MenuInputOption>((val) => ({
      value: val,
      label: val,
      isChecked: true,
    }));
  }

  return [];
}

// ---- Matching ---------------------------------------------------------------

/**
 * Applies value matching to options for both SingleSelect and MultiSelect
 */
export function applyValueMatching<
  T extends {
    value: string | number;
    label?: string;
    isChecked?: boolean;
    secondaryLabel?: string;
  },
>(
  options: T[],
  selectedValue: MenuInputOption | MenuInputOption[] | null | undefined
): T[] {
  if (!selectedValue) {
    return options.map((o) => ({ ...o, isChecked: false }));
  }

  // MultiSelect
  if (Array.isArray(selectedValue)) {
    const selectedValues = selectedValue
      .filter(isOptionLike)
      .map((v) => String(v.value));

    return options.map((o) => ({
      ...o,
      isChecked: selectedValues.includes(String(o.value)),
    }));
  }

  // SingleSelect
  if (isOptionLike(selectedValue)) {
    const selVal = String(selectedValue.value);
    const selLbl = selectedValue.label;

    return options.map((o) => {
      const valueMatch = String(o.value) === selVal;
      const labelMatch = o.label !== undefined && o.label === selLbl;

      return { ...o, isChecked: valueMatch || labelMatch };
    });
  }

  // Fallback
  return options.map((o) => ({ ...o, isChecked: false }));
}
